import { orderHsitoryTypeConstants } from "constants/orderHistory";
import { ToastContext } from "context/ToastContext";
import { parseDate } from "helpers/date";
import { dollarsToCents, centsToDollars, parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { useState, useContext } from "react";
import { useCreateOrderHistoryMutation } from "store/queries/orderHistory";

import {
  StyledProgressBar,
  StyledSectionContainer,
  StyledInputNumber,
  StyledInputTextarea,
} from "../styled";

function BalanceLine({
  title,
  lastPaymentDescription,
  orderId,
  providerId,
  userId,
  onUpdateOrder,
  paymentDescription,
  specialty,
  remainingAmount,
  paidAmount,
  fullAmount,
  lastPaymentDate,
  lastPaymentAmount,
  isOrderPaid,
}: {
  title: string;
  lastPaymentDescription: string;
  paymentDescription: string;
  orderId: string;
  providerId: string;
  userId?: string | undefined;
  onUpdateOrder: () => void;
  specialty: string;
  remainingAmount: number;
  paidAmount: number;
  fullAmount: number;
  lastPaymentDate: string;
  lastPaymentAmount: number;
  isOrderPaid: boolean;
}) {
  const [showReportForm, setShowReportForm] = useState(false);
  const [collapsed, setCollapsed] = useState(remainingAmount === 0);
  const [reportText, setReportText] = useState("");
  const [loading, setLoading] = useState(false);
  const [balanceReported, setBalanceReported] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [createOrderHistory] = useCreateOrderHistoryMutation();
  const { current: toastElement } = useContext(ToastContext);

  const handleReportBalance = async () => {
    setLoading(true);
    try {
      await createOrderHistory({
        type: orderHsitoryTypeConstants.BalanceReported,
        orderId,
        amountInCents: amount ? dollarsToCents(amount) : undefined,
        description: reportText,
        toProviderId: providerId,
        userId,
      });
      onUpdateOrder();
      setAmount(null);
      setReportText("");
      setBalanceReported(true);
      toastElement?.show({
        severity: "success",
        summary: "Balance reported",
        detail: `Thank you - we’ll coordinate with your provider to adjust your balance. Once approved, we’ll update the remaining amount`,
        life: 8000,
      });
    } catch (error) {
      console.error("Error reporting balance", error);
      toastElement?.show({
        severity: "error",
        summary: "Error reporting balance",
        detail: "Please try again later",
        life: 8000,
      });
      setBalanceReported(false);
    } finally {
      setLoading(false);
      setShowReportForm(false);
    }
  };

  return (
    <StyledSectionContainer className="p-3 mb-3">
      <div
        className="flex gap-2 justify-content-between cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setCollapsed(!collapsed);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
      >
        <div className="flex gap-2 align-items-center">
          <i
            className={`pi text-gray-500 transition-transform duration-300 ${
              collapsed ? "pi-chevron-right" : "pi-chevron-down"
            }`}
            style={{ fontSize: "12px" }}
          />
          <div className="flex flex-column gap-1">
            <p className="font-medium text-md black">{title}</p>
            <p className="text-sm font-medium">{specialty}</p>
          </div>
        </div>
        <div className="flex flex-column gap-1">
          <p className="font-medium text-md black text-right">
            {parsePrice(centsToDollars(remainingAmount))}
          </p>
          <p className="text-sm font-medium">Remaining</p>
        </div>
      </div>
      {!collapsed && (
        <>
          <div className="flex md:flex-row flex-column gap-2 justify-content-between">
            <div className="flex flex-column gap-1">
              <p className="text-sm font-normal">Total Amount</p>
              <p className="text-sm font-semibold black">
                {parsePrice(centsToDollars(fullAmount))}
              </p>
            </div>
            <div className="flex flex-column gap-1">
              <p className="text-sm font-normal">Amount paid</p>
              <p className="text-sm font-semibold purple">
                {parsePrice(centsToDollars(paidAmount))}
              </p>
            </div>
            <div className="flex flex-column gap-1">
              <p className="text-sm font-normal">Last payment</p>
              <p className="text-sm black">
                <span className="font-semibold">
                  {parsePrice(centsToDollars(lastPaymentAmount))}{" "}
                </span>
                on {parseDate(lastPaymentDate, true, false, "MMM D, YYYY")}
              </p>
            </div>
          </div>
          <div className="flex flex-column">
            <div className="flex justify-content-between mb-1">
              <p className="text-sm">Payment Progress</p>
              <p className="text-sm">
                {Math.round((paidAmount * 100) / fullAmount) || 0}%
              </p>
            </div>
            <StyledProgressBar
              value={Math.round((paidAmount * 100) / fullAmount)}
              showValue={false}
              height={7}
            />
            {!showReportForm && (
              <Button
                label={balanceReported ? "Report again" : "Report balance"}
                className="p-button-outlined p-button-sm align-self-end mt-3"
                onClick={() => setShowReportForm(true)}
                disabled={isOrderPaid}
              />
            )}
            {showReportForm && (
              <div className="w-full flex flex-column gap-2 mt-2">
                <StyledInputNumber
                  placeholder="Correct Amount"
                  value={amount}
                  currency="USD"
                  locale="en-US"
                  mode="currency"
                  className="align-self-end"
                  onValueChange={({ value }) => setAmount(value)}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                />
                <StyledInputTextarea
                  placeholder="Please add details"
                  value={reportText}
                  rows={2}
                  onChange={(e) => setReportText(e.target.value)}
                />
                <div className="flex w-full justify-content-end gap-2">
                  <Button
                    label="Submit"
                    className="p-button-sm"
                    onClick={handleReportBalance}
                    disabled={!amount || !reportText || loading}
                    loading={loading}
                  />
                  <Button
                    label="Cancel"
                    className="p-button-outlined p-button-sm"
                    onClick={() => setShowReportForm(false)}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </StyledSectionContainer>
  );
}

export default BalanceLine;
